import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { Subject, Subscription } from 'rxjs';
import {tap} from 'rxjs/operators';

import { TinyMceService } from 'webcommon/common-api';
import {ApBaseCVAFormControl, ApHtmlTextContentService} from 'webcommon/core-ui';
import {
  injectParentForm,
  MacroActionValueType,
} from 'webcommon/shared';

import {MacroActionValueDtoExtended, MacroInterfaces, MacroService} from '../ap-macro/index';
import { AutoCompleter, AutoCompleterSetupDto } from '../auto-completer/index';

import {
  ContentCharacterLengthService,
} from '../content-character-length/index';

const defaultToolbar = 'undo redo | styles | bullist numlist | bold strikethrough';
const embeddedToolbar = 'undo redo | styles | bullist | bold';
const textOnlyToolbar = 'undo redo';
// eslint-disable-next-line max-len
const advancedToolbar = ' cut copy undo redo | alignleft aligncenter alignright | fontformats styles fontsize | bold italic underline strikethrough | bullist numlist |  removeformat | help | fullscreen | insertdatetime';


// according to EditorComponent.writeValue, the value of the editor allows null
// so the FormControl in here is <string | null>
// https://github.com/tinymce/tinymce-angular/blob/main/tinymce-angular-component/src/main/ts/editor/editor.component.ts#L89
@Component({
  selector: 'ap-tiny-editor',
  templateUrl: './ap-tiny-editor.component.html',
})
export class ApTinyEditorComponent
extends ApBaseCVAFormControl<FormControl<string | null>>
implements OnInit, OnDestroy {
  protected parentForm = injectParentForm();
  protected get disabled(): boolean {
    return this.innerControl ? this.innerControl.disabled : false;
  }

  constructor(
    private ngZone: NgZone,
    private tinyMceService: TinyMceService,
    private apHtmlTextContentService: ApHtmlTextContentService,
  ) {
    super();
  }

  @ViewChild('editorInstance') editorInstance: EditorComponent;

  @Input() height: number;
  @Input() hideToolbar: boolean;
  @Input() hideStatusBar: boolean;
  @Input() expandTileOnWarning: boolean | undefined;
  @Input() removeFormatting: boolean;  // not used
  @Input() toolbarName: string | undefined;
  @Input() editorName: string; // used as part of elementId, default value for displayName
  @Input() displayName: string;                 // used in invalid form text message, will use editorName if not provided
  @Input() macroService: MacroService | null = null;
  @Input() macroInterfaces: MacroInterfaces;
  @Input() textToInsert?: Subject<string>;
  @Input() inboundError?: Subject<string>;
  @Input() setFocusOnInit = true;               // sets the focus when the editor is initialized (useful for modal)
  @Input() setFocusOnInitCursorAtEnd = true;    // when setFocusOnInit is enabled, puts cursor at end of text
  @Input() autoResize = false;
  @Input() autoResizeMaxHeight: number | null = null;
  @Input() autoResizeMinHeight: number | null = null;
  @Input() maxHeight: number | null = null;
  @Input() fontFamily = 'Roboto';
  @Input() pasteAsText = true;

  // Used to limit the number of characters in the editor.  Parent caller must
  // supply service and call initService() to set max character length.
  // Service is also passed to the AutoCompleter via the Create method, so
  // it can check if the macro action value will exceed the max character length.
  @Input() contentCharacterLengthService: ContentCharacterLengthService | null = null;

  // G3 SigWriter Pharmacy Instructions and Internal Note use this mode
  @Input() oneLineEditorMode = false;

  @Output() dirtyChanged: EventEmitter<void> = new EventEmitter();
  @Output() editorValueChanged = new EventEmitter<string | null>();
  @Output() editorValueChangedTextOnly = new EventEmitter<string>();
  @Output() apBlur: EventEmitter<void> = new EventEmitter();
  @Output() apFocus: EventEmitter<void> = new EventEmitter();

  defaultMinHeight = 120;
  defaultHeight = 400;

  loaded = false;
  tinymceOptions?: EditorComponent['init'];
  autoCompleterSetupDto?: AutoCompleterSetupDto;
  autoCompleter?: AutoCompleter;
  insertSub?: Subscription;
  errorSub?: Subscription;
  editorId: string;
  hasWarning = false;
  showWarningSubject = new Subject<MacroActionValueDtoExtended>();
  useToolbar: string;
  warningMessage = '';
  minHeight = this.defaultMinHeight;
  currentTextLength?: number;
  private useSpellChecker = false;
  contentStyle = '';

  ngOnInit() {
    if (this.oneLineEditorMode) {
      this.contentStyle = 'p { margin-top: 7px; margin-bottom: 7px; } '
        // + ' :hover { background-color: #eff2f7; } '  // this does not update the statusbar hover state
        + ' body::-webkit-scrollbar { width: 12px; background-color: #F5F5F5; } '
        + ' body::-webkit-scrollbar-track { background: #f5f5f5; } '
        + ' body::-webkit-scrollbar-thumb { background: rgba(0, 0, 0, 0.2); } '
        + ' body::-webkit-scrollbar-thumb:hover { background: #999; } '
        + ' body '
        + ' { '
        + ' margin-top: 0px; '
        + ' margin-right: 7.5px; '
        + ' margin-bottom: 0px; '
        + ' margin-left: 7.5px; '
        + ' font-weight: 400; '
        + ' font-size: 14px; '
        + ' font-family: Roboto, sans-serif; '
        + ' color: #3c4858; '
        + ' }';

      this.autoResize = false;
      this.height = 39;
      this.defaultMinHeight = this.height;
      this.defaultHeight = this.height;
      this.minHeight = this.height;
      this.maxHeight = 200;
      this.hideStatusBar = false;
    }

    // Disable spell checker if readonly mode
    // use Tiny Spell Checker is asset valid
    // Otherwise use browser spell checker
    this.useSpellChecker = !this.disabled && this.tinyMceService.isSpellCheckEnabled();

    this.editorId = 'ap-tiny-editor-' + this.editorName;
    this.autoCompleterSetupDto = {
      macroConfigDto: {
        name: 'apEditorAutoCompleter',
        editorName: this.editorName,
        macroInterfaces: this.macroInterfaces,
      },
      macroActionValue: {
        Action: MacroActionValueType.None,
        Value: null,
      },
      onWarningAdded: this.showWarningSubject,
    };
    this.showWarningSubject.subscribe((value) => {
      this.updateWarningLine(value);
    });

    if (!this.hideToolbar) {
      switch (this.toolbarName) {
        case 'embeddedToolbar': {
          this.useToolbar = embeddedToolbar;
          break;
        }
        case 'advancedToolbar': {
          this.useToolbar = advancedToolbar;
          break;
        }
        case 'textOnlyToolbar': {
          this.useToolbar = textOnlyToolbar;
          break;
        }
        default: {
          this.useToolbar = defaultToolbar;
          break;
        }
      }
    }

    if (this.autoResize && this.autoResizeMaxHeight) {
      // use autosize plug to allow window to resize
      this.minHeight = this.autoResizeMinHeight ? this.autoResizeMinHeight : this.defaultMinHeight;
      this.maxHeight = this.autoResizeMaxHeight;
    } else {
      this.minHeight = this.defaultMinHeight;
      this.maxHeight = this.maxHeight ? this.maxHeight : this.height ? this.height : this.defaultHeight;
    }

    let toolbar = this.hideToolbar ? false : this.useToolbar;

    let pluginList = this.autoResize ? 'autoresize fullscreen lists' : 'fullscreen lists';
    if (this.useSpellChecker) {
      pluginList += ' tinymcespellchecker';
      if (!this.hideToolbar) {
        toolbar += ' | spellchecker  spellcheckdialog';
      }
    }

    this.tinymceOptions = {
      branding: false,
      browser_spellcheck: !this.useSpellChecker,
      content_style: this.contentStyle,
      elementpath: false,
      entity_encoding: 'raw',
      // format,
      height: this.height ? this.height : this.defaultHeight,
      max_height: this.maxHeight,
      menubar: false,
      min_height: this.minHeight,
      paste_as_text: this.pasteAsText,
      paste_data_images: false,
      plugins: pluginList,
      spellchecker_active: this.useSpellChecker,
      spellchecker_languages: this.useSpellChecker ? 'English=en_US_medical, Spanish=es' : '',
      remove_linebreaks: true,
      resize: true,
      readonly: true,
      setup: (editor) => {
        editor.on('init', () => {
          if (this.macroService && this.autoCompleterSetupDto) {
            this.autoCompleter = AutoCompleter.create(this.macroService, this.contentCharacterLengthService);
            this.autoCompleter.addAutoCompleter(editor, this.ngZone, this.autoCompleterSetupDto);
          }

          if (!this.oneLineEditorMode) {
            editor.getDoc().body.style.fontFamily = this.fontFamily ? this.fontFamily : 'Roboto';
          }

          if (this.required) {
            editor.getDoc().body.style.backgroundColor = '#fafad2';
          }

          // set focus on editor and put cursor at end of text
          // this call is especially for the modal editors
          if (this.setFocusOnInit === true) {
            this.focusEditor(editor, this.setFocusOnInitCursorAtEnd);

            // for some reason this also refocuses the cursor. Moved to prevent auto focus when there's other fields
            editor.execCommand('mceAutoResize');
          }
        });
        editor.on('focus', () => {
          this.onFocus();
        });
        editor.on('blur', () => {
          this.onBlur();
        });
        editor.on('beforeinput', (e) => {
          if (!this.contentCharacterLengthService) {
            return;
          }

          if (e.inputType === 'insertText') {
            if (e.data && e.data.length > 0) {
              const charLength = e.data.length;
              if (this.contentCharacterLengthService.willContentExceedMaxCharacterLength(charLength)) {
                e.preventDefault();
                return;
              }
            }
          } else if (e.inputType === 'insertParagraph') {
            const contentTextData = editor.getContent({ format: 'text' });

            // insertParagraph inserts two '\n' characters.  We need to account for this.
            if ((contentTextData.length + 2) > this.contentCharacterLengthService.maxCharacterLength) {
              e.preventDefault();
              return;
            }
          }
        });
        editor.on('paste', (e) => {
          if (!this.contentCharacterLengthService) {
            return;
          }

          const pasteString = e.clipboardData?.getData('text');
          if (pasteString && pasteString.length >= 0) {
            const charLength = pasteString.length;

            if (this.contentCharacterLengthService.willContentExceedMaxCharacterLength(charLength)) {
              e.preventDefault();

              const remainingLength = this.contentCharacterLengthService.remainingCharacterLength;
              const truncatedPasteString = pasteString.substring(0, remainingLength);
              if (!this.contentCharacterLengthService.willContentExceedMaxCharacterLength(truncatedPasteString.length)) {
                editor.execCommand('mceInsertContent', false, truncatedPasteString);
              }
            }
          }
        });
      },
      statusbar: !this.hideStatusBar,
      style_formats: [
        { title: 'Heading 1', format: 'h1' },
        { title: 'Heading 2', format: 'h2' },
        { title: 'Heading 3', format: 'h3' },
        { title: 'Heading 4', format: 'h4' },
        { title: 'Heading 5', format: 'h5' },
        { title: 'Heading 6', format: 'h6' },
        { title: 'Paragraph', block: 'p' },
      ],
      toolbar,
      toolbar_mode: 'sliding',
    };

    this.insertSub = this.textToInsert?.subscribe((x) => {
      if (this.contentCharacterLengthService) {
        const charLength = this.apHtmlTextContentService.getTextContentLength(x);
        if (this.contentCharacterLengthService.willContentExceedMaxCharacterLength(charLength)) {
          this.updateWarningLine(this.getMacroActionValueMaxCountExceeded());
          return;
        }
      }

      this.editorInstance.editor.execCommand('mceInsertContent', false, x.replace(/(?:\r\n|\r|\n)/g, '<br>'));
    });

    this.errorSub = this.inboundError?.subscribe((error) => {
      if (!this.autoCompleterSetupDto) {
        return;
      }

      this.autoCompleterSetupDto.macroActionValue.Action = MacroActionValueType.ShowWarning;
      this.autoCompleterSetupDto.macroActionValue.Value = error;
      this.updateWarningLine(this.autoCompleterSetupDto.macroActionValue);
    });

    this.loaded = true;
  }

  override ngOnDestroy(): void {
    this.insertSub?.unsubscribe();
    this.errorSub?.unsubscribe();
    this.showWarningSubject.complete();
    super.ngOnDestroy();
  }

  public getContent(): string {
    return this.innerControl?.value || '';
  }
  public getContentAsText(): string {
    return this.editorInstance.editor.getContent({ format: 'text' });
  }

  protected override setupInnerControlSubs(innerControl: FormControl<string | null>): Subscription | undefined {
    return innerControl.valueChanges.pipe(
      tap((value) => {
        const contentAsText = this.getContentAsText();

        // At most only one of the events should emit
        // These methods should only be used when calling the getContent methods cannot be used.
        this.editorValueChanged.emit(value);
        this.editorValueChangedTextOnly.emit(contentAsText);

        // Update current content character count.
        if (this.contentCharacterLengthService) {
          const curContextTextLength = this.apHtmlTextContentService.getTextContentLength(contentAsText);
          this.contentCharacterLengthService.setCurrentCharacterLength(curContextTextLength);
        }
      }),
    ).subscribe();
  }

  onDirty() {
    this.dirtyChanged.emit();
  }

  onFocus() {
    this.apFocus.next();
  }

  onBlur() {
    this.apBlur.next();
  }

  closeWarning() {
    if (this.autoCompleterSetupDto) {
      this.autoCompleterSetupDto.macroActionValue.Action = MacroActionValueType.None;
      this.autoCompleterSetupDto.macroActionValue.Value = '';
      this.autoCompleterSetupDto.macroActionValue.MacroResultId = '';
    }
    this.warningMessage = '';
    this.hasWarning = false;
  }

  showWarning() {
    // Warning text is shown for AutoCompleter, will need to refactor to support other warning types
    const show = this.autoCompleterSetupDto?.macroActionValue.Action === MacroActionValueType.ShowWarning;
    return show;
  }

  updateWarningLine(macroActionValue: MacroActionValueDtoExtended) {
    this.warningMessage = macroActionValue.Value || '';
    const warning = macroActionValue.Action === MacroActionValueType.ShowWarning;
    if (warning) {
      if (!this.hasWarning) {
        this.hasWarning = true;
      }
    } else {
      if (this.hasWarning) {
        this.hasWarning = false;
      }
    }

    const editorContainer = this.editorInstance?.editor.getContainer();
    if (editorContainer) {
      if (warning && !this.expandTileOnWarning) {
        const calculatedHeight = (this.height ? this.height : this.defaultHeight) - 63;
        editorContainer.style.height = `${calculatedHeight}px`;
      } else {
        editorContainer.style.height = `${(this.height ? this.height : this.defaultHeight)}px`;
      }
    }
  }

  private focusEditor(editor: EditorComponent['editor'], setCursorAtEnd: boolean = true) {
    editor.focus();
    if (setCursorAtEnd === true) {
      editor.selection.select(editor.getBody(), true);
      editor.selection.collapse(false);
    }
  }

  setFocus(setCursorAtEnd: boolean = true) {
    this.focusEditor(this.editorInstance.editor, setCursorAtEnd);
  }

  private getMacroActionValueMaxCountExceeded(): MacroActionValueDtoExtended {
    return {
      Value: 'The content you are inserting would exceed the maximum number of characters allowed.',
      Action: MacroActionValueType.ShowWarning,
    };
  }
}
